import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowRight, Radio, Trophy, Zap, Users, Sparkles, Target, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import WorldClock from "@/components/WorldClock";
import SeasonCountdownBanner from "@/components/SeasonCountdownBanner";

export default function Landing() {
  const [overview, setOverview] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [latestChampion, setLatestChampion] = useState(null);
  const [winStory, setWinStory] = useState(null);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/overview").then((r) => setOverview(r.data)).catch(() => {}),
      api.get("/transfers").then((r) => setTransfers(r.data.slice(0, 8))).catch(() => {}),
      api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}),
      api.get("/players").then((r) => setPlayers(r.data)).catch(() => {}),
      api.get("/champions").then((r) => setLatestChampion(r.data[0] || null)).catch(() => {}),
      api.get("/predictions/featured-win").then((r) => setWinStory(r.data)).catch(() => setWinStory(null)),
      api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => setSponsors([])),
    ]);
  }, []);

  const teamById = (id) => teams.find((t) => t.id === id);
  const playerById = (id) => players.find((p) => p.id === id);
  const champTeam = latestChampion && teamById(latestChampion.winner_team_id);
  const runnerUpTeam = latestChampion && teamById(latestChampion.runner_up_team_id);
  const thirdTeam = latestChampion && teamById(latestChampion.third_team_id);
  const champMvp = latestChampion && playerById(latestChampion.mvp_player_id);

  return (
    <div className="space-y-16">
      <SeasonCountdownBanner />
      {/* SPINNING LEGACY EMBLEM — home-page hero */}
      <div className="flex justify-center pt-4 sm:pt-8" data-testid="home-spinner">
        <div className="relative">
          <div
            className="absolute inset-0 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{
              background: "radial-gradient(circle, rgba(244,208,63,0.45) 0%, rgba(176,132,255,0.25) 40%, transparent 70%)",
            }}
          />
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_esports-legacy/artifacts/9vv3bfrj_LEGACY_HIL_logo.png"
            alt="Legacy · spinning emblem"
            className="relative w-40 h-40 sm:w-56 sm:h-56 lg:w-64 lg:h-64 object-contain drop-shadow-[0_0_36px_rgba(244,208,63,0.55)]"
            style={{ animation: "legacy-spin 12s linear infinite" }}
          />
          <style>{`
            @keyframes legacy-spin {
              from { transform: rotateY(0deg); }
              to   { transform: rotateY(360deg); }
            }
          `}</style>
        </div>
      </div>

      {/* REIGNING CHAMPION PODIUM - 1st / 2nd / 3rd */}
      {latestChampion && champTeam && (
        <Link
          to="/trophies"
          data-testid="pinned-champion-podium"
          className="block border border-[#F4D03F] bg-gradient-to-b from-[#F4D03F]/10 via-transparent to-transparent p-6 sm:p-8 hover:border-yellow-300 transition-colors group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="text-[10px] font-mono tracking-[0.3em] text-[#F4D03F] uppercase flex items-center gap-2">
              <Trophy className="w-4 h-4" /> Season {latestChampion.season} · {latestChampion.year} · Podium
            </div>
            <ArrowRight className="w-5 h-5 text-[#F4D03F] shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end">
            {/* 2nd — left, mid-height */}
            <div data-testid="podium-silver" className={`border ${runnerUpTeam ? "border-gray-400" : "border-[#232330]"} bg-[#101015] p-4 sm:p-6 text-center h-32 sm:h-40 flex flex-col justify-end`}>
              <div className="text-[10px] font-mono tracking-widest text-gray-400 uppercase">2nd</div>
              <div
                className="font-heading font-black uppercase tracking-tighter text-lg sm:text-2xl mt-1 truncate"
                style={{ color: runnerUpTeam?.logo_color || "#888" }}
              >
                {runnerUpTeam?.name || "—"}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mt-1">{runnerUpTeam?.tag || "TBD"}</div>
            </div>
            {/* 1st — center, tallest, gold */}
            <div data-testid="podium-gold" className="border-2 border-[#F4D03F] bg-gradient-to-b from-[#F4D03F]/15 to-transparent p-4 sm:p-6 text-center h-40 sm:h-56 flex flex-col justify-end">
              <Trophy className="w-8 h-8 text-[#F4D03F] mx-auto mb-2" />
              <div className="text-[10px] font-mono tracking-widest text-[#F4D03F] uppercase">Champion</div>
              <div
                className="font-heading font-black uppercase tracking-tighter text-xl sm:text-3xl mt-1 truncate"
                style={{ color: champTeam.logo_color }}
              >
                {champTeam.name}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F]/80 mt-1">{champTeam.tag}</div>
              {champMvp && (
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-gray-400 flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#F4D03F]" /> MVP · {champMvp.handle}
                </div>
              )}
            </div>
            {/* 3rd — right, shortest, bronze */}
            <div data-testid="podium-bronze" className={`border ${thirdTeam ? "border-[#B08A5B]" : "border-[#232330]"} bg-[#101015] p-4 sm:p-6 text-center h-24 sm:h-32 flex flex-col justify-end`}>
              <div className="text-[10px] font-mono tracking-widest text-[#B08A5B] uppercase">3rd</div>
              <div
                className="font-heading font-black uppercase tracking-tighter text-base sm:text-xl mt-1 truncate"
                style={{ color: thirdTeam?.logo_color || "#888" }}
              >
                {thirdTeam?.name || "—"}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-600 mt-1">{thirdTeam?.tag || "TBD"}</div>
            </div>
          </div>
          {latestChampion.ring_note && (
            <div className="mt-5 text-xs text-gray-400 text-center italic line-clamp-2">
              {latestChampion.ring_note}
            </div>
          )}
        </Link>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden border border-[#232330]" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/7862508/pexels-photo-7862508.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05050A] via-[#05050A]/70 to-transparent" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative px-6 sm:px-10 lg:px-16 py-16 lg:py-28 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-8">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_esports-legacy/artifacts/9vv3bfrj_LEGACY_HIL_logo.png"
                alt="Legacy crest"
                className="w-28 sm:w-36 lg:w-48 h-auto drop-shadow-[0_0_40px_rgba(244,208,63,0.5)]"
              />
              <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs flex items-center gap-3">
                <span className="inline-block w-2 h-2 bg-[#FF007F] animate-pulse" />
                CIRCUIT 2026 · SEASON LIVE
              </div>
            </div>
            <h1 className="font-heading font-black tracking-tighter uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.9]">
              Boots on. <span className="text-cyan-400">Comms hot.</span><br />
              Legends <span className="text-[#FF007F]">carved.</span>
            </h1>
            <p className="mt-8 max-w-xl text-gray-300 text-base sm:text-lg leading-relaxed">
              A tactical competition for elite console operators. Sign your streamers, track player rating,
              chase the trophy — all from one broadcast-grade command center.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/streamers/signup">
                <Button
                  size="lg"
                  data-testid="hero-streamer-signup"
                  className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest px-8"
                >
                  Sign a streamer <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/standings">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-view-standings"
                  className="rounded-none border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black uppercase tracking-widest"
                >
                  View standings
                </Button>
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-3">
            {[
              { label: "Teams", val: overview?.teams ?? "—", icon: Users, color: "text-cyan-400" },
              { label: "Athletes", val: overview?.players ?? "—", icon: Trophy, color: "text-[#FF007F]" },
              { label: "Streamers", val: overview?.streamers ?? "—", icon: Radio, color: "text-[#F4D03F]" },
              { label: "Matches", val: overview?.completed_matches ?? "—", icon: Zap, color: "text-white" },
            ].map((s) => (
              <div
                key={s.label}
                className="border border-[#232330] bg-black/60 backdrop-blur-sm p-5 hover:border-cyan-400 transition-colors"
                data-testid={`stat-${s.label.toLowerCase()}`}
              >
                <s.icon className={`w-4 h-4 mb-3 ${s.color}`} />
                <div className="font-heading font-black text-3xl">{s.val}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED WIN STORY */}
      {winStory && winStory.home && winStory.away && (
        <section
          data-testid="featured-win-story"
          className="relative overflow-hidden border-2 border-[#00FFB2]/60 bg-gradient-to-br from-[#00FFB2]/10 via-[#0a0a12] to-[#F4D03F]/10 p-6 lg:p-8"
          style={{
            boxShadow: "0 0 40px rgba(0,255,178,0.18) inset, 0 0 30px rgba(244,208,63,0.1)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
               style={{ backgroundImage: "radial-gradient(#00FFB2 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-[#00FFB2] animate-pulse" />
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#00FFB2]">
                  Biggest correct call · Circuit shout-out
                </div>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="font-heading font-black text-3xl lg:text-4xl uppercase tracking-tighter text-white truncate">
                  {winStory.winner_name}
                </div>
                {winStory.streak > 0 && (
                  <div
                    data-testid="featured-win-streak"
                    className="flex items-center gap-1 text-[#FF007F] font-heading font-black uppercase tracking-widest text-sm"
                  >
                    <Flame className="w-4 h-4" />
                    {winStory.streak}-streak
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-300 mt-2 max-w-xl">
                Called the exact score{" "}
                <span className="text-[#00FFB2] font-bold font-mono">
                  {winStory.called_home}–{winStory.called_away}
                </span>{" "}
                on{" "}
                <span style={{ color: winStory.home.logo_color }} className="font-heading font-black">
                  {winStory.home.tag}
                </span>{" "}
                vs{" "}
                <span style={{ color: winStory.away.logo_color }} className="font-heading font-black">
                  {winStory.away.tag}
                </span>
                {winStory.fixture.map && <span className="text-gray-500"> · {winStory.fixture.map}</span>}.
                Banked <span className="text-[#F4D03F] font-bold">+{winStory.points}</span> pts.
                {winStory.total_correct_picks > 1 && (
                  <span className="text-gray-500"> · {winStory.total_correct_picks - 1} other player{winStory.total_correct_picks === 2 ? "" : "s"} nailed it too.</span>
                )}
              </p>
              <Link
                to="/match"
                data-testid="featured-win-cta"
                className="inline-flex items-center gap-2 mt-4 text-[10px] font-mono uppercase tracking-widest text-black bg-[#00FFB2] hover:bg-emerald-300 px-4 py-2 font-bold"
              >
                <Target className="w-3.5 h-3.5" /> Take your shot
              </Link>
            </div>
            <div className="hidden sm:flex flex-col items-center shrink-0 border-l-2 border-[#00FFB2]/40 pl-5">
              <Trophy className="w-10 h-10 text-[#F4D03F] mb-2" />
              <div className="text-[9px] font-mono uppercase tracking-widest text-gray-500 text-center">Final</div>
              <div className="font-heading font-black text-3xl text-white font-mono">
                {winStory.fixture.home_score}–{winStory.fixture.away_score}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TRANSFER MARQUEE */}
      {transfers.length > 0 && (
        <section
          className="border-y border-[#F4D03F]/50 bg-black overflow-hidden"
          data-testid="transfer-marquee"
        >
          <div className="flex items-center">
            <div className="bg-[#F4D03F] text-black font-heading font-black tracking-widest px-4 py-3 text-xs whitespace-nowrap">
              LIVE TRANSFERS
            </div>
            <div className="overflow-hidden flex-1 relative">
              <div className="flex whitespace-nowrap animate-marquee w-max">
                {[...transfers, ...transfers].map((t, i) => {
                  const p = playerById(t.player_id);
                  const from = teamById(t.from_team_id);
                  const to = teamById(t.to_team_id);
                  return (
                    <span
                      key={i}
                      className="text-[#F4D03F] font-mono text-sm tracking-wider px-8 py-3 flex items-center gap-3"
                    >
                      <span className="text-white font-bold">{p?.handle || "Unknown"}</span>
                      <span className="text-gray-500">·</span>
                      <span>{from?.tag || "FA"}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                      <span>{to?.tag}</span>
                      <span className="text-white">[{t.fee}]</span>
                      <span className={t.status === "Rumour" ? "text-gray-500" : "text-[#00F0FF]"}>
                        · {t.status.toUpperCase()}
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* GLOBAL WORLD CLOCK */}
      <WorldClock />

      {/* MODULES */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { to: "/fixtures", tag: "01", title: "Fixtures", body: "Weekly draw, seed pods, scheduled broadcasts.", color: "border-cyan-400" },
          { to: "/players", tag: "02", title: "Player stats", body: "K/D, MVPs, assists, rating — the whole receipt.", color: "border-[#FF007F]" },
          { to: "/transfers", tag: "03", title: "Transfer market", body: "Confirmed moves, rumours, loan windows.", color: "border-[#F4D03F]" },
        ].map((m) => (
          <Link
            key={m.to}
            to={m.to}
            data-testid={`module-${m.title.toLowerCase().replace(/\s+/g, "-")}`}
            className={`group border ${m.color} bg-[#101015] p-6 hover:-translate-y-0.5 transition-transform`}
          >
            <div className="text-xs font-mono tracking-widest text-gray-500 mb-6">{m.tag} /</div>
            <div className="font-heading text-2xl font-black tracking-tight uppercase mb-2 group-hover:text-cyan-400 transition-colors">
              {m.title}
            </div>
            <div className="text-sm text-gray-400">{m.body}</div>
            <ArrowRight className="w-4 h-4 mt-6 text-cyan-400" />
          </Link>
        ))}
      </section>

      {/* CTA */}
      <section
        className="border border-[#232330] p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        data-testid="cta-section"
      >
        <div>
          <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-3">/ FOR STREAMERS</div>
          <h2 className="font-heading font-black tracking-tighter uppercase text-3xl lg:text-5xl">
            Claim your dossier.
          </h2>
          <p className="text-gray-400 mt-3 max-w-lg">
            Full editable profile, verified badge, schedule sync, and audience metrics that promoters actually read.
          </p>
        </div>
        <Link to="/streamers/signup">
          <Button
            size="lg"
            data-testid="cta-signup"
            className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white font-bold uppercase tracking-widest"
          >
            Enlist now <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </section>

      {/* SPONSORS / PARTNERS */}
      {sponsors.length > 0 && (
        <section data-testid="sponsors-section" className="border-t border-[#232330] pt-10">
          <div className="text-center mb-8">
            <div className="text-[10px] font-mono tracking-[0.4em] text-gray-500 uppercase mb-2">
              / Partners &amp; Sponsors
            </div>
            <h2 className="font-heading font-black uppercase tracking-tighter text-2xl lg:text-3xl text-white">
              Powered by
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {sponsors.map((s) => (
              <a
                key={s.id}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`sponsor-${s.id}`}
                title={s.title}
                className="group border border-[#232330] bg-[#0a0a12] hover:bg-[#101018] hover:border-[#F4D03F] transition-colors p-5 flex flex-col items-center gap-3 aspect-[4/3]"
              >
                <div className="flex-1 flex items-center justify-center w-full">
                  <img
                    src={s.logo_url}
                    alt={s.title}
                    className="max-h-16 lg:max-h-20 max-w-full object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all"
                  />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 group-hover:text-[#F4D03F] transition-colors text-center truncate w-full">
                  {s.title}
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
